import { Box, Text } from "@saleor/macaw-ui-next";
import { ReactNode } from "react";
import { useIntl } from "react-intl";

import { portalLabels as l } from "./labels";
import { formatDay } from "./utils";

export interface RosterCell {
  userId: string;
  date: string;
  label: string;
  color?: string | null;
  late?: boolean;
}

/** People × days grid of the published roster (mapa de horário). */
export const RosterGrid = ({
  days,
  people,
  cells,
  highlightUserId,
  renderCell,
}: {
  days: string[];
  people: Array<{ userId: string; name: string }>;
  cells: RosterCell[];
  highlightUserId?: string | null;
  renderCell?: (userId: string, date: string, cell?: RosterCell) => ReactNode;
}) => {
  const intl = useIntl();
  const byKey = new Map(cells.map(cell => [`${cell.userId}|${cell.date}`, cell]));

  return (
    <Box __overflowX="auto">
      <Box as="table" __borderCollapse="collapse" __minWidth="100%" __fontSize="13px">
        <thead>
          <tr>
            <Box as="th" padding={1} textAlign="left" />
            {days.map(day => (
              <Box as="th" key={day} padding={1} __whiteSpace="nowrap">
                {formatDay(day)}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {people.map(person => (
            <Box
              as="tr"
              key={person.userId}
              backgroundColor={person.userId === highlightUserId ? "accent1" : undefined}
            >
              <Box as="td" padding={1} __whiteSpace="nowrap">
                <Text size={2} fontWeight={person.userId === highlightUserId ? "bold" : undefined}>
                  {person.name}
                </Text>
              </Box>
              {days.map(day => {
                const cell = byKey.get(`${person.userId}|${day}`);

                return (
                  <Box
                    as="td"
                    key={day}
                    padding={1}
                    textAlign="center"
                    borderWidth={1}
                    borderStyle="solid"
                    borderColor="default1"
                    style={cell?.color ? { background: cell.color } : undefined}
                  >
                    {renderCell ? (
                      renderCell(person.userId, day, cell)
                    ) : (
                      <Text size={1}>
                        {cell?.label ?? intl.formatMessage(l.rosterBase)}
                        {cell?.late ? " *" : ""}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};
